import { useState, useEffect, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { analyzeTicketRouting, mergePriorities } from "../utils/serviceDeskRouting";
import { submitTicket, trackTicket } from "../api/serviceDeskApi";
import {
  CONTACT_PHONE,
  CONTACT_PHONE_TEL,
  CONTACT_EMAIL,
  aboutPageStats,
} from "../data/constants";
import AnimatedCounter from "../components/AnimatedCounter";

const howItWorks = [
  {
    step: "01",
    title: "Submit your issue",
    desc: "Fill in the form with issue details. Our system scans your description to route it to the right team.",
  },
  {
    step: "02",
    title: "Get your ticket ID",
    desc: "You receive a unique ticket number instantly. Save it to track progress anytime on this page.",
  },
  {
    step: "03",
    title: "We resolve & update",
    desc: "Our engineers work on your issue and status updates appear in your ticket timeline.",
  },
];

const ISSUE_TYPES = ["Network", "Cloud", "Firewall", "VPN", "Server", "Software", "Other"];
const PRIORITIES = ["Low", "Medium", "High", "Critical"];

const MAX_ATTACHMENT_BYTES = 1_200_000;

const initialForm = {
  fullName: "",
  email: "",
  company: "",
  phone: "",
  issueType: "",
  issueTitle: "",
  issueDescription: "",
  priority: "Medium",
};

function formatStatus(s) {
  const map = { open: "Open", in_progress: "In Progress", resolved: "Resolved", closed: "Closed" };
  return map[s] || s;
}

function statusClass(s) {
  const map = {
    open: "service-desk-status--open",
    in_progress: "service-desk-status--progress",
    resolved: "service-desk-status--resolved",
    closed: "service-desk-status--closed",
  };
  return map[s] || "service-desk-status--open";
}

export default function ServiceDeskPage() {
  const [searchParams] = useSearchParams();
  const ticketFromUrl = (searchParams.get("ticket") || searchParams.get("track") || "").trim();

  const [panel, setPanel] = useState("submit");
  const [form, setForm] = useState(initialForm);
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [attachmentName, setAttachmentName] = useState("");
  const [attachmentError, setAttachmentError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submittedId, setSubmittedId] = useState(null);
  const [lastSubmittedEmail, setLastSubmittedEmail] = useState("");
  const [formError, setFormError] = useState("");

  const [trackId, setTrackId] = useState("");
  const [tracked, setTracked] = useState(null);
  const [trackError, setTrackError] = useState("");

  const scanText = useMemo(() => `${form.issueTitle}\n${form.issueDescription}`, [form.issueTitle, form.issueDescription]);
  const [debouncedScan, setDebouncedScan] = useState(scanText);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedScan(scanText), 320);
    return () => clearTimeout(t);
  }, [scanText]);

  useEffect(() => {
    if (!ticketFromUrl) return;
    setPanel("track");
    setTrackId(ticketFromUrl);
    setTrackError("");
    setTracked(null);
    let cancelled = false;
    (async () => {
      try {
        const t = await trackTicket(ticketFromUrl);
        if (cancelled) return;
        if (!t) {
          setTrackError("No ticket found with that number.");
          return;
        }
        setTracked(t);
      } catch (err) {
        if (!cancelled) setTrackError(err?.message || "Could not look up ticket.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ticketFromUrl]);

  const routing = useMemo(() => analyzeTicketRouting(debouncedScan), [debouncedScan]);
  const effectivePriority = useMemo(
    () => mergePriorities(form.priority, routing.routingPriority),
    [form.priority, routing.routingPriority]
  );

  const onFile = useCallback((e) => {
    const file = e.target.files?.[0];
    setAttachmentError("");
    if (!file) {
      setAttachmentFile(null);
      setAttachmentName("");
      return;
    }
    if (file.size > MAX_ATTACHMENT_BYTES) {
      setAttachmentError(`File is too large (max ${Math.round(MAX_ATTACHMENT_BYTES / 1000)} KB).`);
      e.target.value = "";
      setAttachmentFile(null);
      setAttachmentName("");
      return;
    }
    setAttachmentFile(file);
    setAttachmentName(file.name);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    if (!form.fullName.trim() || !form.email.trim() || !form.company.trim()) {
      setFormError("Please fill in all required fields.");
      return;
    }
    if (!form.issueType || !form.issueTitle.trim() || !form.issueDescription.trim() || !form.priority) {
      setFormError("Please complete issue details and priority.");
      return;
    }
    const route = analyzeTicketRouting(`${form.issueTitle}\n${form.issueDescription}`);
    const eff = mergePriorities(form.priority, route.routingPriority);
    const emailForConfirm = form.email.trim();
    const formData = new FormData();
    formData.append("fullName", form.fullName.trim());
    formData.append("email", emailForConfirm);
    formData.append("company", form.company.trim());
    formData.append("phone", form.phone.trim());
    formData.append("issueType", form.issueType);
    formData.append("issueTitle", form.issueTitle.trim());
    formData.append("issueDescription", form.issueDescription.trim());
    formData.append("priority", form.priority);
    formData.append("routedTeam", route.team);
    formData.append("routingPriority", route.routingPriority);
    formData.append("effectivePriority", eff);
    if (attachmentFile) {
      formData.append("attachment", attachmentFile, attachmentFile.name);
    }
    setSubmitting(true);
    try {
      const data = await submitTicket(formData);
      setSubmittedId(data.ticket.id);
      setLastSubmittedEmail(emailForConfirm);
      setForm(initialForm);
      setAttachmentFile(null);
      setAttachmentName("");
    } catch (err) {
      setFormError(err?.message || "Could not submit ticket. Check API (XAMPP) and .env.development.");
    } finally {
      setSubmitting(false);
    }
  };

  const lookupTicket = async (e) => {
    e.preventDefault();
    setTrackError("");
    setTracked(null);
    const id = trackId.trim();
    if (!id) {
      setTrackError("Enter your ticket number.");
      return;
    }
    try {
      const t = await trackTicket(id);
      if (!t) {
        setTrackError("No ticket found with that number. Check the ID from your confirmation.");
        return;
      }
      setTracked(t);
    } catch (err) {
      setTrackError(err?.message || "Could not look up ticket.");
    }
  };

  const renderSidebar = () => (
    <div className="service-desk-sidebar">
      {panel === "submit" && (
        <>
          <div className="contact-page-section-head contact-page-section-head--sidebar">
            <span>Smart Routing</span>
          </div>
          <p className="contact-page-sidebar-desc">
            As you type, we detect keywords and assign the right team. Priority may be raised automatically for urgent issues.
          </p>
          <div className="service-desk-routing-card">
            <div className="service-desk-routing-grid">
              <div className="service-desk-routing-item">
                <span className="service-desk-routing-label">Assigned Team</span>
                <strong>{routing.team}</strong>
              </div>
              <div className="service-desk-routing-item">
                <span className="service-desk-routing-label">Detected Urgency</span>
                <strong>{routing.routingPriority}</strong>
              </div>
              <div className="service-desk-routing-item">
                <span className="service-desk-routing-label">Your Priority</span>
                <strong>{form.priority}</strong>
              </div>
              <div className="service-desk-routing-item">
                <span className="service-desk-routing-label">Effective Priority</span>
                <span className={`service-desk-pill service-desk-pill--${effectivePriority.toLowerCase()}`}>
                  {effectivePriority}
                </span>
              </div>
            </div>
          </div>
          <div className="service-desk-hint">
            <i className="bi bi-lightbulb" aria-hidden="true"></i>
            <span>
              <strong>Keyword hints:</strong> &ldquo;no internet&rdquo; → Network (Critical); &ldquo;VPN&rdquo; / &ldquo;firewall&rdquo; → Security (High); &ldquo;Azure&rdquo;, &ldquo;server&rdquo; → respective teams.
            </span>
          </div>
        </>
      )}

      <div className="contact-page-section-head contact-page-section-head--sidebar">
        <span>How It Works</span>
      </div>
      <ol className="contact-page-steps">
        {howItWorks.map((item) => (
          <li key={item.step} className="contact-page-step">
            <span className="contact-page-step-num">{item.step}</span>
            <div>
              <strong>{item.title}</strong>
              <p>{item.desc}</p>
            </div>
          </li>
        ))}
      </ol>

      <div className="contact-page-section-head contact-page-section-head--sidebar">
        <span>Need Urgent Help?</span>
      </div>
      <p className="contact-page-sidebar-desc">
        For critical outages, call us directly and our on-call team will assist immediately.
      </p>
      <div className="contact-page-info-list">
        <div className="contact-page-info-item">
          <div className="contact-page-info-icon">
            <i className="bi bi-telephone-fill" aria-hidden="true"></i>
          </div>
          <div>
            <span className="contact-page-info-label">Phone</span>
            <a className="contact-page-info-val" href={`tel:${CONTACT_PHONE_TEL}`}>
              {CONTACT_PHONE}
            </a>
          </div>
        </div>
        <div className="contact-page-info-item">
          <div className="contact-page-info-icon">
            <i className="bi bi-envelope-fill" aria-hidden="true"></i>
          </div>
          <div>
            <span className="contact-page-info-label">Email</span>
            <a className="contact-page-info-val" href={`mailto:${CONTACT_EMAIL}`}>
              {CONTACT_EMAIL}
            </a>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="contact-page service-desk-page">
      <section className="contact-page-hero animate-on-scroll">
        <div className="container">
          <div className="row g-4 g-lg-5 align-items-center">
            <div className="col-lg-7">
              <span className="contact-page-label">Support</span>
              <h1 className="contact-page-hero-title">
                Report &amp; track your{" "}
                <span className="contact-page-accent">IT issues.</span>
              </h1>
              <p className="contact-page-hero-desc">
                Log a technical complaint and track it anytime with your unique ticket number.
                Our smart routing assigns the right team instantly — updates appear as we progress your request.
              </p>
            </div>
            <div className="col-lg-5">
              <div className="contact-page-stats-card">
                {aboutPageStats.map((stat, i) => (
                  <div
                    key={stat.label}
                    className={`contact-page-stat${i % 2 === 0 ? " has-right-border" : ""}${i < 2 ? " has-bottom-border" : ""}`}
                  >
                    <AnimatedCounter
                      value={stat.value}
                      suffix={stat.suffix}
                      className="contact-page-stat-num"
                      as="div"
                    />
                    <span className="contact-page-stat-label">{stat.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="contact-page-form-section animate-on-scroll">
        <div className="container">
          <div className="service-desk-tab-switch">
            <button
              type="button"
              className={`contact-page-tag${panel === "submit" ? " is-active" : ""}`}
              onClick={() => setPanel("submit")}
            >
              <i className="bi bi-plus-circle" aria-hidden="true"></i>
              Submit a Complaint
            </button>
            <button
              type="button"
              className={`contact-page-tag${panel === "track" ? " is-active" : ""}`}
              onClick={() => setPanel("track")}
            >
              <i className="bi bi-search" aria-hidden="true"></i>
              Track Ticket
            </button>
          </div>

          {panel === "submit" && (
            <div className="row g-5">
              <div className="col-lg-8">
                <form className="contact-page-form" onSubmit={handleSubmit}>
                  {submittedId && (
                    <div className="contact-page-success">
                      <i className="bi bi-check-circle-fill" aria-hidden="true"></i>
                      <div>
                        <strong>Ticket created:</strong>{" "}
                        <code className="service-desk-ticket-code">{submittedId}</code>
                        <p className="service-desk-success-note">
                          Save this number to track progress. We will reach you at{" "}
                          {lastSubmittedEmail || "your email"} when there are updates.
                        </p>
                        <button
                          type="button"
                          className="service-desk-link-btn"
                          onClick={() => {
                            setSubmittedId(null);
                            setLastSubmittedEmail("");
                          }}
                        >
                          Submit another ticket
                        </button>
                      </div>
                    </div>
                  )}
                  {formError && (
                    <div className="service-desk-alert service-desk-alert--error">
                      <i className="bi bi-exclamation-circle-fill" aria-hidden="true"></i>
                      {formError}
                    </div>
                  )}

                  <div className="contact-page-section-head">
                    <span>Submit a Complaint</span>
                    <div className="contact-page-section-line" />
                  </div>
                  <p className="contact-page-form-desc">
                    Describe your issue in detail. Our system will route it to the right specialist team automatically.
                  </p>

                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="contact-page-label-field">
                        Full Name<span className="contact-page-req">*</span>
                      </label>
                      <input
                        className="contact-page-input"
                        placeholder="Your name"
                        required
                        value={form.fullName}
                        onChange={(e) => setForm({ ...form, fullName: e.target.value })}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="contact-page-label-field">
                        Email Address<span className="contact-page-req">*</span>
                      </label>
                      <input
                        type="email"
                        className="contact-page-input"
                        placeholder="you@company.com"
                        required
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="contact-page-label-field">
                        Company Name<span className="contact-page-req">*</span>
                      </label>
                      <input
                        className="contact-page-input"
                        placeholder="Your organization"
                        required
                        value={form.company}
                        onChange={(e) => setForm({ ...form, company: e.target.value })}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="contact-page-label-field">Phone Number</label>
                      <input
                        type="tel"
                        className="contact-page-input"
                        placeholder="+91 xxxxx xxxxx"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="contact-page-label-field">
                        Issue Type<span className="contact-page-req">*</span>
                      </label>
                      <select
                        className="contact-page-input"
                        required
                        value={form.issueType}
                        onChange={(e) => setForm({ ...form, issueType: e.target.value })}
                      >
                        <option value="">Select issue type...</option>
                        {ISSUE_TYPES.map((x) => (
                          <option key={x} value={x}>{x}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="contact-page-label-field">
                        Priority<span className="contact-page-req">*</span>
                      </label>
                      <select
                        className="contact-page-input"
                        required
                        value={form.priority}
                        onChange={(e) => setForm({ ...form, priority: e.target.value })}
                      >
                        {PRIORITIES.map((x) => (
                          <option key={x} value={x}>{x}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-12">
                      <label className="contact-page-label-field">
                        Issue Title<span className="contact-page-req">*</span>
                      </label>
                      <input
                        className="contact-page-input"
                        placeholder="Short summary of the problem"
                        required
                        value={form.issueTitle}
                        onChange={(e) => setForm({ ...form, issueTitle: e.target.value })}
                      />
                    </div>
                    <div className="col-12">
                      <label className="contact-page-label-field">
                        Issue Description<span className="contact-page-req">*</span>
                      </label>
                      <textarea
                        className="contact-page-textarea"
                        rows={5}
                        placeholder="Describe the problem in detail. We scan this text to route your ticket to the right team."
                        required
                        value={form.issueDescription}
                        onChange={(e) => setForm({ ...form, issueDescription: e.target.value })}
                        style={{ marginBottom: 0 }}
                      />
                    </div>
                    <div className="col-12">
                      <label className="contact-page-label-field">Attachments</label>
                      <input
                        type="file"
                        className="contact-page-input"
                        onChange={onFile}
                        accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/*,.txt,.log,.zip"
                      />
                      {attachmentError && (
                        <div className="service-desk-field-error">{attachmentError}</div>
                      )}
                      {attachmentName && (
                        <div className="service-desk-file-selected">
                          <i className="bi bi-paperclip" aria-hidden="true"></i>
                          {attachmentName}
                        </div>
                      )}
                    </div>
                    <div className="col-12">
                      <button type="submit" className="contact-page-submit" disabled={submitting}>
                        <i className="bi bi-send-fill" aria-hidden="true"></i>
                        {submitting ? "Submitting…" : "Submit Ticket"}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
              <div className="col-lg-4">{renderSidebar()}</div>
            </div>
          )}

          {panel === "track" && (
            <div className="row g-5">
              <div className="col-lg-8">
                <form className="contact-page-form" onSubmit={lookupTicket}>
                  <div className="contact-page-section-head">
                    <span>Track Your Ticket</span>
                    <div className="contact-page-section-line" />
                  </div>
                  <p className="contact-page-form-desc">
                    Enter the ticket number you received after submission (e.g. EMI-20260328-AB12C).
                  </p>

                  <div className="row g-3 align-items-end">
                    <div className="col-md-8">
                      <label className="contact-page-label-field">Ticket Number</label>
                      <input
                        className="contact-page-input service-desk-ticket-input"
                        value={trackId}
                        onChange={(e) => setTrackId(e.target.value)}
                        placeholder="EMI-YYYYMMDD-XXXXX"
                      />
                    </div>
                    <div className="col-md-4">
                      <button type="submit" className="contact-page-submit">
                        <i className="bi bi-search" aria-hidden="true"></i>
                        Look Up
                      </button>
                    </div>
                  </div>

                  {trackError && (
                    <div className="service-desk-alert service-desk-alert--warning mt-4">
                      <i className="bi bi-exclamation-triangle-fill" aria-hidden="true"></i>
                      {trackError}
                    </div>
                  )}

                  {tracked && (
                    <div className="service-desk-track-result mt-4">
                      <div className="service-desk-track-header">
                        <div>
                          <span className="service-desk-routing-label">Ticket ID</span>
                          <code className="service-desk-ticket-code service-desk-ticket-code--lg">{tracked.id}</code>
                        </div>
                        <span className={`service-desk-status ${statusClass(tracked.status)}`}>
                          {formatStatus(tracked.status)}
                        </span>
                      </div>

                      <div className="service-desk-track-meta">
                        <div className="service-desk-track-meta-item">
                          <span className="service-desk-routing-label">Team</span>
                          <strong>{tracked.routedTeam}</strong>
                        </div>
                        <div className="service-desk-track-meta-item">
                          <span className="service-desk-routing-label">Priority</span>
                          <span className={`service-desk-pill service-desk-pill--${String(tracked.effectivePriority).toLowerCase()}`}>
                            {tracked.effectivePriority}
                          </span>
                        </div>
                        <div className="service-desk-track-meta-item">
                          <span className="service-desk-routing-label">Issue</span>
                          <strong>{tracked.issueTitle}</strong>
                        </div>
                        <div className="service-desk-track-meta-item">
                          <span className="service-desk-routing-label">Type</span>
                          <strong>{tracked.issueType}</strong>
                        </div>
                      </div>

                      <div className="service-desk-track-desc">
                        <span className="service-desk-routing-label">Description</span>
                        <p>{tracked.issueDescription}</p>
                      </div>

                      {(tracked.attachment?.downloadUrl || tracked.attachment?.dataUrl) && (
                        <div className="service-desk-track-attachment">
                          <span className="service-desk-routing-label">Attachment</span>
                          <a
                            href={tracked.attachment.downloadUrl || tracked.attachment.dataUrl}
                            download={tracked.attachment.fileName}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <i className="bi bi-download" aria-hidden="true"></i>
                            {tracked.attachment.fileName}
                          </a>
                        </div>
                      )}

                      {tracked.statusLog?.length > 0 && (
                        <div className="service-desk-timeline-wrap">
                          <span className="service-desk-routing-label">Activity Timeline</span>
                          <ul className="service-desk-timeline list-unstyled mb-0">
                            {[...tracked.statusLog].reverse().map((log, i) => (
                              <li key={i} className="service-desk-timeline-item">
                                <div className="service-desk-timeline-dot" aria-hidden="true"></div>
                                <div className="service-desk-timeline-content">
                                  <div className="service-desk-timeline-status">
                                    {formatStatus(log.status)}
                                  </div>
                                  <div className="service-desk-timeline-meta">
                                    {new Date(log.at).toLocaleString()}
                                    {log.note ? ` · ${log.note}` : ""}
                                    <span className="service-desk-timeline-by"> ({log.by})</span>
                                  </div>
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </form>
              </div>
              <div className="col-lg-4">{renderSidebar()}</div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
